import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProductById } from '../services/api';
import CategoryAssignEditor from './sync/CategoryAssignEditor';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await getProductById(id);
        setProduct(data);
      } catch (err) {
        setError('Failed to load product details. Please try again later.');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const formatPrice = (price) => {
    if (!price) return 'Price not available';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const renderDescription = (description) => {
    if (!description) return 'No description available';
    
    // If description contains HTML, render it as HTML
    if (description.includes('<')) {
      return <div dangerouslySetInnerHTML={{ __html: description }} />;
    }
    
    // Otherwise render as plain text with line breaks
    return description.split('\n').map((line, index) => (
      <p key={index}>{line}</p>
    ));
  };

  if (loading) {
    return <div className="loading">Loading product details...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">{error}</div>
        <Link to="/products" className="back-button">
          ← Back to Products
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="error-container">
        <div className="error">Product not found</div>
        <Link to="/products" className="back-button">
          ← Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="product-detail">
      <div className="product-detail-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <div className="breadcrumb">
          <Link to="/products">Products</Link> / {product.product_name || product.sku}
        </div>
      </div>

      <div className="product-detail-content">
        <div className="product-main-info">
          <div className="product-header">
            <h1>{product.product_name || product.sku || 'Unnamed Product'}</h1>
            <span className="product-sku">SKU: {product.sku}</span>
          </div>

          {product.price && (
            <div className="product-price-section">
              <span className="product-price">{formatPrice(product.price)}</span>
            </div>
          )}

          <div className="product-meta-info">
            <div className="meta-item">
              <strong>Akeneo categories:</strong>{' '}
              {(product.akeneo_categories || []).length === 0 ? (
                <em>(none — uncategorised)</em>
              ) : (
                product.akeneo_categories.map((c) => (
                  <code key={c} style={{ marginRight: 6 }}>{c}</code>
                ))
              )}
              <button
                type="button"
                onClick={() => setShowCategoryEditor(true)}
                style={{ marginLeft: 8 }}
              >
                Edit…
              </button>
            </div>
            {product.brand && (
              <div className="meta-item">
                <strong>Brand:</strong> {product.brand}
              </div>
            )}
            {product.status && (
              <div className="meta-item">
                <strong>Status:</strong> 
                <span className={`status ${product.status.toLowerCase()}`}>
                  {product.status}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="product-description-section">
          <h2>Description</h2>
          <div className="product-description">
            {renderDescription(product.description)}
          </div>
        </div>

        {product.specifications && (
          <div className="product-specifications">
            <h2>Specifications</h2>
            <div className="specifications-content">
              {typeof product.specifications === 'object' ? (
                <dl className="spec-list">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="spec-item">
                      <dt>{key}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p>{product.specifications}</p>
              )}
            </div>
          </div>
        )}

        {(product.weight || product.dimensions) && (
          <div className="product-physical-info">
            <h2>Physical Information</h2>
            <div className="physical-info-grid">
              {product.weight && (
                <div className="info-item">
                  <strong>Weight:</strong> {product.weight}
                </div>
              )}
              {product.dimensions && (
                <div className="info-item">
                  <strong>Dimensions:</strong> {product.dimensions}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showCategoryEditor && product?.sku && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(560px, 100%)',
            background: '#0c1320',
            color: '#dde',
            borderLeft: '1px solid #2a3a52',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.4)',
            zIndex: 1000,
            overflowY: 'auto',
            padding: 16,
          }}
        >
          <button
            type="button"
            onClick={() => setShowCategoryEditor(false)}
            style={{ float: 'right' }}
          >
            ✕
          </button>
          <CategoryAssignEditor sku={product.sku} onSaved={() => setShowCategoryEditor(false)} />
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
